import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const NEW = "__new__";

/** Adds the checked price-list items to a group — an existing one, or a new one created here. */
export default function AddToGroupDialog({ open, onOpenChange, itemIds, groups, onDone, onCreateGroup }) {
  const [groupId, setGroupId] = useState(groups.length ? "" : NEW);
  const [newName, setNewName] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const creating = groupId === NEW;

  const add = async () => {
    setBusy(true);
    try {
      let target = groupId;
      if (creating) {                    // create it here so nobody has to leave the dialog
        const g = await estApi.createGroup({ name: newName.trim() });
        target = g.id;
      }
      const res = await estApi.addItemsToGroup(target, itemIds, parseFloat(qty) || 1);
      if (creating) toast.success(`Group "${newName.trim()}" created with ${res.added} item(s)`);
      else toast.success(`${res.added} added${res.skipped ? `, ${res.skipped} already in the group` : ""}`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to group");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-to-group-dialog">
        <DialogHeader>
          <DialogTitle>Add {itemIds.length} item{itemIds.length === 1 ? "" : "s"} to a group</DialogTitle>
          <DialogDescription>Groups expand into individual estimate lines when used.</DialogDescription>
        </DialogHeader>

        <>
            <label className="block">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Group</span>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs"
                      data-testid="add-to-group-select">
                <option value="">— choose a group —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name} (v{g.version})</option>)}
                <option value={NEW}>+ New group…</option>
              </select>
            </label>
            {creating && (
              <label className="block">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  New group name
                </span>
                <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                       placeholder="e.g. Standard ADA restroom"
                       className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs"
                       data-testid="add-to-group-new-name" />
                <span className="mt-1 block text-[10px] text-slate-400">
                  The {itemIds.length} checked item(s) are saved into it straight away.
                </span>
              </label>
            )}
            <label className="block">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Qty multiplier</span>
              <input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)}
                     className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs"
                     data-testid="add-to-group-qty" />
            </label>
        </>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                  data-testid="add-to-group-cancel">Cancel</Button>
          <Button size="sm" onClick={add} data-testid="add-to-group-confirm"
                  disabled={busy || !groupId || (creating && !newName.trim())}>
            {creating ? "Create & add" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
